/* eslint-disable @typescript-eslint/no-explicit-any */
import { Cart, LineItem, DiscountAllocation } from '@api-client/src/types';
import { AgnosticTotals } from '@vue-storefront/core';

import { Price } from '../types';
import { formatSelectedAttributeList } from './_utils';

const getCartItems = (cart?: Cart | null): LineItem[] => {
  return cart?.lineItems || [];
};

const getCartItemName = (lineItem: LineItem): string => lineItem.title || "Product's name";

const getCartItemId = (lineItem: LineItem): string => lineItem.id || '';

const getProductId = (lineItem: LineItem): string => lineItem.productId || '';

const getCartItemPartNumber = (lineItem: LineItem): string => lineItem.partNumber || '';

const getCartItemImage = (lineItem: LineItem): string => {
  if (lineItem.imageSrc) {
    const imgPath = lineItem.imageSrc?.substring(0, lineItem.imageSrc?.lastIndexOf('.'));
    const imgExt = lineItem.imageSrc?.split('.')?.pop();
    return `${imgPath}_120x120.${imgExt}`;
  }
  return '';
};

const getCartItemPrice = (lineItem: LineItem): Price => {
  const productPrice: Price = {
    price: null,
    compareToPrice: null,
  };

  if (lineItem) {
    if (lineItem.compareAtPriceV2) {
      productPrice.price = lineItem?.priceV2;
      productPrice.compareToPrice = lineItem.compareAtPriceV2;
    } else {
      productPrice.price = lineItem.priceV2;
    }
  }

  return productPrice;
};

const getPrice = (lineItem: LineItem) => {
  if (getCartItemPrice(lineItem)?.compareToPrice) {
    return getCartItemPrice(lineItem)?.compareToPrice;
  }

  return getCartItemPrice(lineItem)?.price;
};

const getCompareToPrice = (product: any) => {
  if (getCartItemPrice(product)?.compareToPrice) {
    return getCartItemPrice(product)?.price;
  }

  return null;
};

const getCartItemQuantity = (lineItem: LineItem): number => lineItem?.quantity || 0;

const isFreeSale = (lineItem: LineItem) =>
  lineItem.availableForSale && lineItem.totalInventory != null && lineItem.totalInventory <= 0;

const getCartItemMaximumQuantity = (
  lineItem: LineItem,
  options: { supportFreeSale?: boolean; digitalProductMaxQuantity?: number } = {
    supportFreeSale: false,
  },
): number => {
  if (lineItem?.requiresShipping === false) {
    return lineItem.availableForSale ? options.digitalProductMaxQuantity ?? 100 : 0;
  }

  return options.supportFreeSale && isFreeSale(lineItem) ? 100 : lineItem?.totalInventory || 0;
};

const getCartItemAttributes = (lineItem: LineItem) => {
  const formatAttedattributeList = formatSelectedAttributeList(lineItem.selectedOptions);
  if (formatAttedattributeList?.length) {
    const attribArr: any = {};
    formatAttedattributeList?.forEach((attr) => {
      attribArr[attr?.name as any] = attr?.value;
    });
    return { ...attribArr };
  }
  return {};
};

const getCartItemSku = (lineItem: LineItem) => lineItem.sku || '-';

const getProductTags = (lineItem: LineItem) => lineItem.tags;

const getCartTotals = (cart?: Cart | null): AgnosticTotals => {
  const totals: AgnosticTotals = {
    total: 0,
    subtotal: 0,
    special: 0,
  };

  if (cart) {
    return {
      total: cart.totalAmount || 0,
      subtotal: cart.subtotalAmount || 0,
      special: cart.subtotalAmount || 0,
    };
  }

  return totals;
};

const getcheckoutURL = (cart?: Cart | null): string => {
  return cart?.checkoutUrl || '';
};

const getCartTotalItems = (cart?: Cart | null): number => {
  if (cart?.lineItems && Array.isArray(cart.lineItems) && cart.lineItems.length > 0) {
    return cart.lineItems.reduce((acc, { quantity }) => acc + (quantity ?? 0), 0);
  }
  return 0;
};

const getVendor = (lineItem: LineItem): string => {
  return lineItem.productVendor || '';
};

const getDiscountAllocations = (lineItem: LineItem): DiscountAllocation[] =>
  lineItem.discountAllocations;

const cartGetters = {
  getTotals: getCartTotals,
  getItems: getCartItems,
  getItemName: getCartItemName,
  getItemImage: getCartItemImage,
  getItemId: getCartItemId,
  getProductId,
  getItemPrice: getCartItemPrice,
  getItemAttributes: getCartItemAttributes,
  getItemSku: getCartItemSku,
  getTotalItems: getCartTotalItems,
  getPrice,
  getCompareToPrice,
  getCartItemQuantity,
  getCartItemMaximumQuantity,
  getVendor,
  getPartNumber: getCartItemPartNumber,
  getProductTags,
  getcheckoutURL,
  getDiscountAllocations,
};

export default cartGetters;
